import React, { useMemo } from 'react';
import Styled from './styled';
import {
    isAllowedUrl,
    useTipTapViewer
} from '../../utils/TipTap';
import { EditorContent } from '@tiptap/react';
import { ReceiptIcon } from '../index';
import { MessageParticipantRole } from '@amzn/ring-cs-chatbot-service-model';

export type BubbleProps = {
    /** Accessibility support (alt text/aria-label, defaults to 'chat message') */  
    ariaLabel?: string;
    /** The status of the badge, defaults to 'bot'. */
    badge?: MessageParticipantRole;
    /** Optional text for the badge. */
    badgeText?: string;
    /** Determines chat bubble alignment - left (false) | right (true) */
    customer?: boolean;
    /** Children content to display inside the chat bubble. */
    children: string;
    /** Optional, isActionable boolean which we pass to TipTapViewer */
    isActionable?: boolean;
    /** Optional receipt status: 'DELIVERED', 'READ', or undefined */
    receipt?: 'DELIVERED' | 'READ';
    /** Optional sources (title and url) to display below the main content */
    sources?: Array<{ title: string; url: string }>;
    /** Optional title for sources section (defaults to 'Helpful Links') */
    sourceTitle?: string;
    /** Optional, selectedOptions which we pass to TipTapViewer */
    selectedOption?: string;
    /** Send Chat Message via Viewer Components (ex: Select) */
    sendMessage: (text: string) => Promise<void>;
};

/**
 * Chat Bubble component, orientation of the bubble is based on customer prop. 
 * Badge is optional and will appear at the top of the chat bubble
 * 
 * @param {BubbleProps} props - The properties to configure the Bubble component.
 * @returns {JSX.Element} The rendered Bubble component.
 */
export const Bubble: React.FC<BubbleProps> = ({
    ariaLabel = 'chat message',
    badge,
    badgeText,
    customer = false,
    children,
    isActionable = true,
    receipt,
    sources,
    sourceTitle = 'Helpful Links',
    selectedOption,
    sendMessage,
}) => {
    // utilize TipTap to view sanitized HTML
    const viewer = useTipTapViewer({
        message: children,
        sendMessage,
        textOnly: customer,
        isActionable,
        selectedOption,
        ariaLabel: ariaLabel,
    });

    // Filter sources to only include allowed urls
    const allowedSources = useMemo(() => {
        return sources?.filter((source) => isAllowedUrl(source.url)) || [];
    }, [sources]);

    return (
        <Styled.Wrapper customer={customer}>
            <Styled.Bubble customer={customer}>
                {badge && <Styled.Badge>{badgeText}</Styled.Badge>}
                <Styled.Content>
                    <EditorContent editor={viewer} />
                </Styled.Content>
                {allowedSources.length > 0 && (
                    <>
                        <Styled.Divider />
                        <Styled.SourceTitle>{sourceTitle}:</Styled.SourceTitle>
                        <Styled.Sources>
                            <ul>
                                {allowedSources.map((source, index) => (
                                    <li key={index}>
                                        <a href={source.url} target='_blank' rel='noopener noreferrer'>
                                            {source.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Styled.Sources>
                    </>
                )}
                {receipt &&
                    <Styled.ReceiptIndicator receipt={receipt}>
                        <ReceiptIcon />
                    </Styled.ReceiptIndicator>
                }
            </Styled.Bubble>
        </Styled.Wrapper>
    );
};
